// Permissions tab — inline ACL matrix for workflow resource.
// Labels are View/Edit, but BE action identifiers are read/write.
// "Manage" button opens ResourceShareDialog for dialog-driven edit entry point
// (useful from list-page share button flow).
// Inherited rows are readonly — admin must navigate to source (folder/role)
// to modify; creating direct override silently here would confuse audit trail.

import { useState } from "react";
import { useTranslations } from "use-intl";
import { Share2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ResourcePermissionMatrix,
  type ActionDef,
} from "@/components/shared/permissions/resource-permission-matrix";
import { ResourceShareDialog } from "@/components/shared/permissions/resource-share-dialog";
import type { Workflow } from "@/types/workflow.types";

interface PermissionsTabProps {
  workflow: Workflow;
}

// Labels come from i18n at render time; keys are stable BE action identifiers.
const ACTION_KEYS: Array<{ key: string; i18nKey: string }> = [
  { key: "read", i18nKey: "action.view" },
  { key: "write", i18nKey: "action.edit" },
  { key: "run", i18nKey: "action.run" },
  { key: "approve_checkpoint", i18nKey: "action.approve_checkpoint" },
];

export function PermissionsTab({ workflow }: PermissionsTabProps) {
  const t = useTranslations("permissions");
  const tWf = useTranslations("workflows");

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const actions: ActionDef[] = ACTION_KEYS.map((a) => ({
    key: a.key,
    labelKey: `permissions:${a.i18nKey}`,
    label: t(a.i18nKey as Parameters<typeof t>[0]),
  }));

  return (
    <div className="max-w-6xl">
      <section className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--card)]">
        <div className="flex items-center justify-between gap-3 border-b border-[var(--border)] px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <ShieldCheck className="size-4 shrink-0 text-[var(--muted-foreground)]" aria-hidden />
            <h3 className="truncate text-sm font-semibold text-[var(--foreground)]">
              {tWf("tabs.permissions")}
            </h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsDialogOpen(true)}
          >
            <Share2 className="size-3.5" aria-hidden />
            {tWf("permissions.manage")}
          </Button>
        </div>

        <div className="p-3">
          {/* Inline matrix — current-state at-a-glance */}
          <ResourcePermissionMatrix
            resourceType="workflow"
            resourceId={workflow.id}
            actions={actions}
            showPrincipalPicker={false}
            compactEmpty
          />
        </div>
      </section>

      {/* Full dialog — same matrix in modal context; entry for list-page share */}
      <ResourceShareDialog
        resourceType="workflow"
        resourceId={workflow.id}
        actions={actions}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={tWf("permissions.dialog_title", { name: workflow.name })}
      />
    </div>
  );
}

export default PermissionsTab;
